import RecordImporter from '@/components/ingest/work-record-importer';
import ImageBatchUploader from '@/components/ingest/image-uploader';

import { useState } from 'react';
import { 
  FileText, 
  Upload, 
  Info,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  MapPin,
  Send,
  Download,
  AlertCircle,
  CheckCircle,
  Image,
  Grid,
  List,
  Settings,
  RefreshCw
} from 'lucide-react';

// Import your actual components
// import RecordImporter from '@/components/ingest/work-record-importer';
// import ImageBatchUploader from '@/components/ingest/image-uploader';

// Instructions Guide Component
function InstructionsGuide() {
  const [expandedSections, setExpandedSections] = useState({
    recordImporter: true,
    imageUploader: false,
    troubleshooting: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg shadow-lg p-8 mb-8 text-white">
          <h1 className="text-4xl font-bold mb-3">Data Import Tools Guide</h1>
          <p className="text-blue-100 text-lg">Learn how to use the Record Importer and Batch Image Uploader</p>
        </div>

        {/* Record Importer Section */}
        <div className="bg-white rounded-lg shadow-md mb-6 overflow-hidden">
          <button
            onClick={() => toggleSection('recordImporter')}
            className="w-full flex items-center justify-between p-6 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Upload className="w-6 h-6 text-blue-600" />
              </div>
              <div className="text-left">
                <h2 className="text-2xl font-bold text-gray-800">Record Importer</h2>
                <p className="text-sm text-gray-600">Import CSV data with column mapping</p>
              </div>
            </div>
            {expandedSections.recordImporter ? (
              <ChevronDown className="w-6 h-6 text-gray-400" />
            ) : (
              <ChevronRight className="w-6 h-6 text-gray-400" />
            )}
          </button>

          {expandedSections.recordImporter && (
            <div className="p-6 pt-0 border-t">
              {/* Step 1 */}
              <div className="mb-8">
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                      1
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-800 mb-3">Verify Your Connection</h3>
                    <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-3">
                      <p className="text-sm text-blue-900">
                        <strong>Look for the blue information box</strong> at the top of the page to verify:
                      </p>
                    </div>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <span>Your <strong>email address</strong> is displayed</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <span>The correct <strong>Project ID</strong> is shown</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <span>Your <strong>role</strong> (admin, user, etc.) is correct</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Step 2 */}
              <div className="mb-8">
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                      2
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      Load Available Fields
                      <RefreshCw className="w-5 h-5 text-blue-600" />
                    </h3>
                    <p className="text-gray-700 mb-3">
                      The system automatically loads available target fields when you open the page. You can refresh them at any time.
                    </p>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <p className="text-sm font-semibold text-gray-700 mb-2">Available Target Fields section shows:</p>
                      <ul className="space-y-1 text-sm text-gray-600">
                        <li>• Number of fields loaded (e.g., "4 fields loaded from API")</li>
                        <li>• Click <strong>Refresh</strong> button to reload the latest fields</li>
                        <li>• If loading fails, default fields will be used</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 3 */}
              <div className="mb-8">
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                      3
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      Upload Your CSV File
                      <Upload className="w-5 h-5 text-blue-600" />
                    </h3>
                    <div className="space-y-3 text-gray-700">
                      <p><strong>To upload:</strong></p>
                      <ul className="space-y-2 ml-4">
                        <li className="flex items-start gap-2">
                          <span className="text-blue-600 font-bold">→</span>
                          <span>Click the dashed upload area, or</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-blue-600 font-bold">→</span>
                          <span>Drag and drop your CSV file into the box</span>
                        </li>
                      </ul>
                      <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mt-4">
                        <div className="flex gap-2">
                          <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0" />
                          <div className="text-sm">
                            <p className="font-semibold text-yellow-900 mb-1">Important:</p>
                            <ul className="text-yellow-800 space-y-1">
                              <li>• Only CSV files are supported</li>
                              <li>• Your CSV must have headers in the first row</li>
                              <li>• Headers will be used for column mapping</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 4 */}
              <div className="mb-8">
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                      4
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      Map Your Columns
                      <MapPin className="w-5 h-5 text-blue-600" />
                    </h3>
                    <p className="text-gray-700 mb-3">
                      After uploading, you'll see a <strong>Column Mapping</strong> section with all your CSV columns.
                    </p>
                    <div className="bg-gray-50 rounded-lg p-4 mb-3">
                      <p className="font-semibold text-gray-800 mb-2">For each column:</p>
                      <ul className="space-y-2 text-gray-700">
                        <li className="flex items-start gap-2">
                          <span className="text-blue-600 font-bold">1.</span>
                          <span>You'll see the column name and a sample value</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-blue-600 font-bold">2.</span>
                          <span>Use the dropdown to select a target field</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-blue-600 font-bold">3.</span>
                          <span>Leave unmapped if you don't need that column</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-blue-600 font-bold">4.</span>
                          <span>Fields already used show "(already mapped)"</span>
                        </li>
                      </ul>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <div className="flex gap-2">
                        <Info className="w-5 h-5 text-blue-600 flex-shrink-0" />
                        <p className="text-sm text-blue-900">
                          Check the top right corner to see your progress (e.g., "3 of 5 columns mapped")
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 5 */}
              <div className="mb-8">
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                      5
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-800 mb-3">Process Your Mapping</h3>
                    <p className="text-gray-700 mb-3">
                      Once you've mapped at least one column, click the <strong className="text-blue-600">Process Mapping</strong> button.
                    </p>
                    <div className="bg-green-50 border-l-4 border-green-500 p-4">
                      <p className="text-sm text-green-900">
                        <strong>What happens:</strong> The system transforms your CSV data using your column mappings and shows a preview of the final data structure.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 6 */}
              <div className="mb-8">
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                      6
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      Submit or Download
                      <Send className="w-5 h-5 text-blue-600" />
                    </h3>
                    <p className="text-gray-700 mb-3">You have two options:</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Send className="w-5 h-5 text-blue-600" />
                          <h4 className="font-semibold text-blue-900">Insert Work Requests</h4>
                        </div>
                        <p className="text-sm text-blue-800">
                          Sends your mapped data directly to the backend database for processing.
                        </p>
                      </div>
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Download className="w-5 h-5 text-green-600" />
                          <h4 className="font-semibold text-green-900">Download CSV</h4>
                        </div>
                        <p className="text-sm text-green-800">
                          Downloads the mapped data as a CSV file to your computer for review.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 7 */}
              <div className="mb-4">
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                      7
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-800 mb-3">Review Results</h3>
                    <p className="text-gray-700 mb-3">
                      After submitting, each row in your data will show a status indicator:
                    </p>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                        <div>
                          <p className="font-semibold text-green-900">Successfully inserted</p>
                          <p className="text-sm text-green-700">Record was added to the database</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
                        <div>
                          <p className="font-semibold text-red-900">Duplicate record</p>
                          <p className="text-sm text-red-700">A matching record already exists</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
                        <div>
                          <p className="font-semibold text-red-900">Validation error</p>
                          <p className="text-sm text-red-700">Required field missing or invalid data</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                      <p className="text-sm text-blue-900">
                        <strong>Summary box shows:</strong> Total inserted records, duplicates found, and validation errors encountered.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Image Uploader Section */}
        <div className="bg-white rounded-lg shadow-md mb-6 overflow-hidden">
          <button
            onClick={() => toggleSection('imageUploader')}
            className="w-full flex items-center justify-between p-6 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                <Image className="w-6 h-6 text-indigo-600" />
              </div>
              <div className="text-left">
                <h2 className="text-2xl font-bold text-gray-800">Batch Image Uploader</h2>
                <p className="text-sm text-gray-600">Upload and validate multiple images at once</p>
              </div>
            </div>
            {expandedSections.imageUploader ? (
              <ChevronDown className="w-6 h-6 text-gray-400" />
            ) : (
              <ChevronRight className="w-6 h-6 text-gray-400" />
            )}
          </button>

          {expandedSections.imageUploader && (
            <div className="p-6 pt-0 border-t">
              {/* Step 1 */}
              <div className="mb-8">
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                      1
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      Check Validation Rules
                      <Info className="w-5 h-5 text-indigo-600" />
                    </h3>
                    <p className="text-gray-700 mb-3">
                      Before uploading, review the validation requirements by clicking the <strong>Info button</strong> (ⓘ) in the top right.
                    </p>
                    <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                      <p className="font-semibold text-indigo-900 mb-2">Validation rules include:</p>
                      <ul className="space-y-1 text-sm text-indigo-800">
                        <li>• <strong>Image dimensions:</strong> Min/max width and height in pixels</li>
                        <li>• <strong>File size:</strong> Minimum and maximum file size limits</li>
                        <li>• <strong>Allowed formats:</strong> .jpg, .jpeg, .png, .gif, .webp</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 2 */}
              <div className="mb-8">
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                      2
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-800 mb-3">Choose Your View Mode</h3>
                    <p className="text-gray-700 mb-3">Select how you want to see your images:</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Grid className="w-5 h-5 text-gray-700" />
                          <h4 className="font-semibold text-gray-900">Grid View</h4>
                        </div>
                        <p className="text-sm text-gray-700">
                          Shows images as cards in a grid layout. Best for seeing many images at once.
                        </p>
                      </div>
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <List className="w-5 h-5 text-gray-700" />
                          <h4 className="font-semibold text-gray-900">List View</h4>
                        </div>
                        <p className="text-sm text-gray-700">
                          Shows images in a detailed list with dimensions and error messages. Best for troubleshooting.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 3 */}
              <div className="mb-8">
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                      3
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      Upload Your Images
                      <Upload className="w-5 h-5 text-indigo-600" />
                    </h3>
                    <div className="space-y-3 text-gray-700">
                      <p><strong>To upload images:</strong></p>
                      <ul className="space-y-2 ml-4">
                        <li className="flex items-start gap-2">
                          <span className="text-indigo-600 font-bold">→</span>
                          <span>Click the upload area, or</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-indigo-600 font-bold">→</span>
                          <span>Drag and drop multiple images into the box</span>
                        </li>
                      </ul>
                      <div className="bg-green-50 border-l-4 border-green-500 p-4 mt-4">
                        <div className="flex gap-2">
                          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                          <div className="text-sm">
                            <p className="font-semibold text-green-900 mb-1">Automatic Validation:</p>
                            <p className="text-green-800">
                              Each image is automatically validated as you upload. Valid images show a preview, invalid images show error details.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 4 */}
              <div className="mb-8">
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                      4
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-800 mb-3">Review Validation Results</h3>
                    <p className="text-gray-700 mb-3">Check your uploaded images:</p>
                    <div className="space-y-3">
                      <div className="flex items-start gap-3 p-3 bg-white border border-gray-200 rounded-lg">
                        <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                        <div>
                          <p className="font-semibold text-gray-900">Valid Images</p>
                          <p className="text-sm text-gray-700">Show image preview with filename and size</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                        <div>
                          <p className="font-semibold text-red-900">Invalid Images</p>
                          <p className="text-sm text-red-700 mb-2">Show specific validation errors, such as:</p>
                          <ul className="text-xs text-red-600 space-y-1 ml-4">
                            <li>• Width/height below or above limits</li>
                            <li>• File size too small or too large</li>
                            <li>• Invalid file format</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                      <div className="flex gap-2">
                        <Info className="w-5 h-5 text-blue-600 flex-shrink-0" />
                        <p className="text-sm text-blue-900">
                          <strong>Statistics bar shows:</strong> Number of valid images, invalid images, and uploaded images.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 5 */}
              <div className="mb-8">
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                      5
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-800 mb-3">Remove Unwanted Images (Optional)</h3>
                    <p className="text-gray-700 mb-3">You can remove images before uploading:</p>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start gap-2">
                        <span className="text-indigo-600 font-bold">•</span>
                        <span>Click the <strong>X</strong> button on any image card to remove it</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-indigo-600 font-bold">•</span>
                        <span>Click <strong>Clear All</strong> to remove all images</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-indigo-600 font-bold">•</span>
                        <span>Click <strong>Clear Completed</strong> to remove successfully uploaded images</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Step 6 */}
              <div className="mb-8">
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                      6
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      Upload Valid Images
                      <Send className="w-5 h-5 text-indigo-600" />
                    </h3>
                    <p className="text-gray-700 mb-3">
                      Click the <strong className="text-blue-600">Upload</strong> button to send all valid images to the backend.
                    </p>
                    <div className="space-y-3">
                      <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4">
                        <div className="flex gap-2">
                          <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0" />
                          <div className="text-sm">
                            <p className="font-semibold text-yellow-900 mb-1">Note:</p>
                            <ul className="text-yellow-800 space-y-1">
                              <li>• Only valid images will be uploaded</li>
                              <li>• Invalid images are automatically skipped</li>
                              <li>• A progress bar shows upload status</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 7 */}
              <div className="mb-4">
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                      7
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-800 mb-3">Monitor Upload Progress</h3>
                    <p className="text-gray-700 mb-3">Watch the status of each image:</p>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="w-6 h-6 border-3 border-blue-600 border-t-transparent rounded-full animate-spin flex-shrink-0"></div>
                        <div>
                          <p className="font-semibold text-blue-900">Uploading...</p>
                          <p className="text-sm text-blue-700">Image is being sent to the server</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                        <div>
                          <p className="font-semibold text-green-900">Upload successful</p>
                          <p className="text-sm text-green-700">Image uploaded and processed</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
                        <div>
                          <p className="font-semibold text-red-900">Upload failed</p>
                          <p className="text-sm text-red-700">Server error or connection issue</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
                      <p className="text-sm text-green-900">
                        <strong>Success message shows:</strong> Total images uploaded successfully and any that failed.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Troubleshooting Section */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <button
            onClick={() => toggleSection('troubleshooting')}
            className="w-full flex items-center justify-between p-6 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="text-left">
                <h2 className="text-2xl font-bold text-gray-800">Troubleshooting</h2>
                <p className="text-sm text-gray-600">Common issues and solutions</p>
              </div>
            </div>
            {expandedSections.troubleshooting ? (
              <ChevronDown className="w-6 h-6 text-gray-400" />
            ) : (
              <ChevronRight className="w-6 h-6 text-gray-400" />
            )}
          </button>

          {expandedSections.troubleshooting && (
            <div className="p-6 pt-0 border-t space-y-6">
              {/* Record Importer Issues */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Record Importer Issues</h3>
                
                <div className="space-y-4">
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">❌ CSV won't upload</h4>
                    <ul className="text-sm text-gray-700 space-y-1 ml-4">
                      <li>• Make sure the file has a .csv extension</li>
                      <li>• Check that the first row contains headers</li>
                      <li>• Verify the file isn't corrupted or password-protected</li>
                    </ul>
                  </div>

                  <div className="border border-gray-200 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">❌ No target fields available</h4>
                    <ul className="text-sm text-gray-700 space-y-1 ml-4">
                      <li>• Click the Refresh button to reload fields</li>
                      <li>• Check your internet connection</li>
                      <li>• Verify you're logged in with the correct account</li>
                      <li>• Contact your administrator if fields still don't load</li>
                    </ul>
                  </div>

                  <div className="border border-gray-200 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">❌ All records showing as duplicates</h4>
                    <ul className="text-sm text-gray-700 space-y-1 ml-4">
                      <li>• These records already exist in the database</li>
                      <li>• Check the existing record details in the status message</li>
                      <li>• Verify you haven't already uploaded this data</li>
                    </ul>
                  </div>

                  <div className="border border-gray-200 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">❌ Validation errors on records</h4>
                    <ul className="text-sm text-gray-700 space-y-1 ml-4">
                      <li>• Check the status column for specific error messages</li>
                      <li>• Common issues: missing required fields, invalid email formats</li>
                      <li>• Fix errors in your CSV and re-upload</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Image Uploader Issues */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Image Uploader Issues</h3>
                
                <div className="space-y-4">
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">❌ Images marked as invalid</h4>
                    <ul className="text-sm text-gray-700 space-y-1 ml-4">
                      <li>• Look at the specific error messages on each image</li>
                      <li>• Click the Info button to see all validation rules</li>
                      <li>• Resize images if dimensions are too large or small</li>
                      <li>• Compress images if file size exceeds limits</li>
                      <li>• Convert to an allowed format (.jpg, .png, .gif, .webp)</li>
                    </ul>
                  </div>

                  <div className="border border-gray-200 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">❌ Upload button is disabled</h4>
                    <ul className="text-sm text-gray-700 space-y-1 ml-4">
                      <li>• Make sure you have at least one valid image</li>
                      <li>• Check if all images have already been uploaded</li>
                      <li>• Invalid images won't prevent uploading valid ones</li>
                    </ul>
                  </div>

                  <div className="border border-gray-200 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">❌ Upload failed errors</h4>
                    <ul className="text-sm text-gray-700 space-y-1 ml-4">
                      <li>• Check your internet connection</li>
                      <li>• Try uploading fewer images at once</li>
                      <li>• Refresh the page and try again</li>
                      <li>• Contact support if errors persist</li>
                    </ul>
                  </div>

                  <div className="border border-gray-200 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">❌ Can't see my images clearly</h4>
                    <ul className="text-sm text-gray-700 space-y-1 ml-4">
                      <li>• Switch between Grid and List view using the toggle buttons</li>
                      <li>• Grid view: Better for quick visual overview</li>
                      <li>• List view: Shows more details and dimensions</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* General Tips */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-5">
                <h3 className="text-lg font-semibold text-blue-900 mb-3 flex items-center gap-2">
                  <Info className="w-5 h-5" />
                  General Tips
                </h3>
                <ul className="text-sm text-blue-800 space-y-2">
                  <li>• Both tools auto-save your progress (Image Uploader only)</li>
                  <li>• You can safely refresh the page without losing your work</li>
                  <li>• Always check validation messages before contacting support</li>
                  <li>• Keep your browser updated for best performance</li>
                  <li>• Use Chrome, Firefox, or Edge for optimal compatibility</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-gray-600">
          <p>Need more help? Contact your system administrator or support team.</p>
        </div>
      </div>
    </div>
  );
}

// Sidebar Menu Component
const SidebarMenu = ({ 
  menuItems, 
  defaultExpanded = false, 
  backgroundColor = 'bg-gray-700',
  activeColor = 'bg-blue-600',
  hoverColor = 'hover:bg-gray-800'
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [currentPath, setCurrentPath] = useState(`/${menuItems[0]?.id || ''}`);

  const activeMenuItem = menuItems.find(item => `/${item.id}` === currentPath) || menuItems[0];

  const navigateTo = (id) => {
    setCurrentPath(`/${id}`);
  };

  const renderContent = () => {
    if (activeMenuItem && activeMenuItem.component) {
      const Component = activeMenuItem.component;
      return <Component />;
    }
    return null;
  };

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar */}
      <div 
        className={`${backgroundColor} text-white transition-all duration-300 ease-in-out ${
          isExpanded ? 'w-64' : 'w-20'
        } relative overflow-hidden flex flex-col shadow-lg`}
      >
        <div className="p-4 flex-1">
          {/* Toggle Button */}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className={`w-12 h-12 p-2 mb-6 rounded-lg ${hoverColor} transition-colors flex items-center justify-center`}
            title={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            {isExpanded ? (
              <ChevronLeft className="w-5 h-5" />
            ) : (
              <ChevronRight className="w-5 h-5" />
            )}
          </button>
          
          {/* Navigation */}
          <nav>
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeMenuItem?.id === item.id;
              
              return (
                <button
                  key={item.id}
                  onClick={() => navigateTo(item.id)}
                  className={`w-full flex items-center px-3 py-3 mb-2 rounded-lg transition-all duration-200 ${
                    isActive 
                      ? `${activeColor} text-white shadow-md` 
                      : `${hoverColor} text-gray-300 hover:text-white`
                  }`}
                  title={!isExpanded ? item.label : ''}
                >
                  <Icon className="w-6 h-6 flex-shrink-0" />
                  <span className={`ml-3 whitespace-nowrap transition-all duration-300 ${
                    isExpanded ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4 w-0'
                  }`}>
                    {item.label}
                  </span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-auto bg-gray-50">
        {renderContent()}
      </div>
    </div>
  );
};

// Main App Component
export default function DataIngestTable() {
  const menuConfig = [
    { 
      id: 'records', 
      label: 'Record Importer', 
      icon: FileText, 
      component: RecordImporter 
    },
    { 
      id: 'images', 
      label: 'Image Uploader', 
      icon: Upload, 
      component: ImageBatchUploader 
    },
    { 
      id: 'instructions', 
      label: 'Instructions', 
      icon: Info, 
      component: InstructionsGuide 
    }
  ];

  return (
    <SidebarMenu 
      menuItems={menuConfig}
      defaultExpanded={false}
      backgroundColor="bg-gray-700"
      activeColor="bg-blue-600"
      hoverColor="hover:bg-gray-800"
    />
  );
}